import React, { useEffect, useState } from 'react';
import { X, History, User, Calendar, Gauge, TrendingUp } from 'lucide-react';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const PAGE_SIZE = 20;

const MeterHistoryModal = ({ isOpen, onClose, assetNum }) => {
    const { t } = useTranslation(); // 🎯 تفعيل الـ Hook
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (isOpen) {
            setPage(1);
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/meter-readings/history/${assetNum}`);
                    setHistory(res.data);
                } catch (err) {
                    console.error("Failed to load meter history");
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, assetNum]);

    if (!isOpen) return null;

    const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
    const pageItems = history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <div className="bg-surface-card w-full max-w-4xl shadow-2xl rounded-sm flex flex-col max-h-[85vh] border border-line">

                {/* Header */}
                <div className="bg-primary-dark text-ink-inverse p-3 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <History size={18} className="text-accent" />
                        <div className="flex flex-col">
                            <span className="font-black text-[12px] uppercase tracking-tighter">{t('meterHistory.title')}</span>
                            <span className="text-[10px] text-ink-inverse/70 uppercase font-bold">{t('meterHistory.lblAsset')}: {assetNum}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-status-danger p-1 rounded transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto bg-surface-panel flex-1">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-20 gap-4">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">{t('meterHistory.fetching')}</span>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="text-center p-20 text-ink-secondary italic text-sm">{t('meterHistory.noRecords')}</div>
                    ) : (
                        <div className="bg-surface-card border border-line shadow-sm">
                            <table className="w-full text-[11px] border-collapse text-left">
                                <thead className="bg-surface-panel border-b-2 border-line text-primary font-black uppercase tracking-widest">
                                    <tr>
                                        <th className="p-3 border-r border-line">{t('meterHistory.thMeterName')}</th>
                                        <th className="p-3 border-r border-line text-center">{t('meterHistory.thReadingVal')}</th>
                                        <th className="p-3 border-r border-line">{t('meterHistory.thReadingDate')}</th>
                                        <th className="p-3 border-r border-line">{t('meterHistory.thEnteredBy')}</th>
                                        <th className="p-3">{t('meterHistory.thEntryDate')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle">
                                    {pageItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-primary-soft transition-all font-bold">
                                            <td className="p-3 border-r border-line">
                                                <div className="flex items-center gap-2">
                                                    <Gauge size={14} className="text-primary" />
                                                    <span className="text-primary">{item.meter_num}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 border-r border-line text-center">
                                                <div className="flex items-center justify-center gap-1 text-status-success">
                                                    <TrendingUp size={12} />
                                                    <span className="text-[13px]">{item.reading_value.toLocaleString()}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 border-r border-line">
                                                <div className="flex items-center gap-2 text-ink-secondary">
                                                    <Calendar size={12} className="text-ink-secondary" />
                                                    {new Date(item.reading_date).toLocaleString('sv-SE').slice(0, 16)}
                                                </div>
                                            </td>
                                            <td className="p-3 border-r border-line text-ink-secondary">
                                                <div className="flex items-center gap-2 uppercase text-[10px]">
                                                    <User size={12} className="text-primary" />
                                                    {item.enter_by}
                                                </div>
                                            </td>
                                            <td className="p-3 text-ink-secondary font-normal italic">
                                                {new Date(item.enter_date).toLocaleString('sv-SE').slice(0, 16)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-surface-panel p-4 border-t border-line flex items-center justify-between">
                    {!loading && history.length > 0 && (
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="px-3 py-1.5 rounded border border-line text-ink-secondary font-black text-[10px] uppercase hover:bg-primary-soft hover:text-primary disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                {t('common.prev', 'Prev')}
                            </button>
                            <span className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">
                                {t('common.page', 'Page')} {page} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="px-3 py-1.5 rounded border border-line text-ink-secondary font-black text-[10px] uppercase hover:bg-primary-soft hover:text-primary disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                {t('common.next', 'Next')}
                            </button>
                        </div>
                    )}
                    <button
                        onClick={onClose}
                        className="px-8 py-2 bg-primary-dark text-ink-inverse font-black text-[10px] uppercase shadow-lg hover:bg-primary transition-all ms-auto"
                    >
                        {t('meterHistory.btnClose')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MeterHistoryModal;